/**
 * Domain hook for User Management
 * Phase 2: Multi-User Auth Foundation
 *
 * Provides CRUD operations for user accounts with RBAC enforcement.
 * Only ADMIN role can manage users.
 *
 * @example
 * const { users, addUser, updateUser, deactivateUser, resetPassword } = useUsers();
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  getAllUsers,
  createUser,
  updateUser as updateUserAuth,
  deactivateUser as deactivateUserAuth,
  reactivateUser as reactivateUserAuth,
  changePassword,
  ROLES
} from '../../platform/auth/authService';
import { useAuth } from '../../components/tailwind/auth/AuthGate';
import { validatePasswordStrength } from '../../platform/auth/passwordUtils';
// FT-101: Import Azure API client for production user management
import azureApiClient, { isAzureApiConfigured } from '../../lib/azureApiClient';

// FT-100: Valid RBAC role values (lowercase) - what the UI form actually sends
// Note: ROLES from authService uses uppercase (CREW, ADMIN, MASTER) - legacy auth system
const VALID_RBAC_ROLES = ['super_admin', 'fleet', 'skipper', 'crew'];

// FT-101: Map frontend RBAC roles to Azure API roles
// API accepts: 'user', 'admin', 'super_admin'
// RBAC role is stored separately for client-side sign-off permissions
const RBAC_TO_API_ROLE = {
  'crew': 'user',
  'skipper': 'user',
  'fleet': 'admin',
  'super_admin': 'super_admin'
};

/**
 * User management hook
 * @returns {Object} User management functions and state
 */
export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();

  // FT-98: Check if current user can manage users (super_admin or fleet)
  // Roles are stored as RBAC values: 'super_admin', 'fleet', 'skipper', 'crew'
  const isAdmin = useMemo(() => {
    const role = auth.user?.role?.toLowerCase();
    return role === 'super_admin' || role === 'fleet';
  }, [auth.user?.role]);

  // FT-98: Count active admins (super_admin or fleet roles)
  const countActiveAdmins = useMemo(() => {
    return users.filter(u => {
      const role = u.role?.toLowerCase();
      return (role === 'super_admin' || role === 'fleet') && u.isActive;
    }).length;
  }, [users]);

  // FT-98: Check if user is the last active admin
  const isLastActiveAdmin = useCallback((userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return false;

    const role = user.role?.toLowerCase();
    // Only an active admin can be "last admin"
    if ((role !== 'super_admin' && role !== 'fleet') || !user.isActive) {
      return false;
    }

    // Check if this is the only active admin
    return countActiveAdmins === 1;
  }, [users, countActiveAdmins]);

  // Refresh user list
  const refresh = useCallback(async () => {
    if (!isAdmin) {
      setUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      let allUsers = [];

      // FT-101: Use Azure API in production, localStorage in dev mode
      if (isAzureApiConfigured()) {
        const result = await azureApiClient.adminGetUsers();
        if (result.success) {
          // Map API users to frontend format
          allUsers = (result.data || []).map(u => ({
            id: u.id,
            name: u.name,
            email: u.email,
            // Use rbacRole if stored, otherwise map API role back to RBAC
            role: u.rbacRole || (u.role === 'admin' ? 'fleet' : u.role === 'user' ? 'crew' : u.role),
            jobTitle: u.jobTitle || null,
            isActive: u.isActive !== false, // Default to active
            createdAt: u.createdAt ? new Date(u.createdAt).getTime() : Date.now(),
            lastLogin: u.lastLoginAt ? new Date(u.lastLoginAt).getTime() : null
          }));
        } else {
          console.error('[useUsers] Failed to fetch from Azure API:', result.error);
          // Fallback to localStorage on API error
          allUsers = getAllUsers();
        }
      } else {
        // Dev mode: use localStorage
        allUsers = getAllUsers();
      }

      // FT-100: Merge current user into list if not present
      // Current user may come from Azure/external auth, not in sr.auth.users
      let mergedUsers = [...allUsers];
      const currentUserId = auth.user?.id;

      if (currentUserId && !allUsers.find(u => u.id === currentUserId)) {
        // Add current user to list (external auth user managing the system)
        mergedUsers.unshift({
          id: currentUserId,
          name: auth.user.name || 'Current User',
          email: auth.user.email || '',
          role: auth.user.role || 'super_admin',
          isActive: true,
          isSelf: true, // Flag for UI to disable self-actions
          createdAt: Date.now(),
          lastLogin: Date.now()
        });
      } else if (currentUserId) {
        // Mark existing user as self
        mergedUsers = mergedUsers.map(u =>
          u.id === currentUserId ? { ...u, isSelf: true } : u
        );
      }

      setUsers(mergedUsers);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('[useUsers] Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, auth.user]);

  // Load users on mount
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Add a new user
  const addUser = useCallback(async (userData, password) => {
    if (!isAdmin) {
      throw new Error('Permission denied: Only administrators can create users');
    }

    // FT-103: Skip password validation in Azure mode (API generates temp password)
    if (!isAzureApiConfigured()) {
      const strength = validatePasswordStrength(password);
      if (!strength.valid) {
        throw new Error(strength.errors.join('. '));
      }
    }

    // Validate required fields
    if (!userData.name?.trim()) {
      throw new Error('Name is required');
    }
    if (!userData.email?.trim()) {
      throw new Error('Email is required');
    }
    if (!userData.role) {
      throw new Error('Role is required');
    }
    // FT-100: Validate against RBAC roles (lowercase), not legacy ROLES (uppercase)
    if (!VALID_RBAC_ROLES.includes(userData.role)) {
      throw new Error('Invalid role');
    }

    try {
      // FT-101: Use Azure API in production, localStorage in dev mode
      if (isAzureApiConfigured()) {
        // Map RBAC role to API role and include organizationId
        // FT-103: Password field is ignored by API — API generates temp password
        const apiUserData = {
          name: userData.name.trim(),
          email: userData.email.trim().toLowerCase(),
          organizationId: auth.user?.organizationId,
          role: RBAC_TO_API_ROLE[userData.role] || 'user',
          // Store RBAC role as metadata for client-side permissions
          rbacRole: userData.role,
          jobTitle: userData.jobTitle || null
        };

        const result = await azureApiClient.adminCreateUser(apiUserData);
        if (!result.success) {
          throw new Error(result.error || 'Failed to create user');
        }

        refresh();
        // FT-103: Return both user and tempPassword from API
        return {
          user: result.data,
          tempPassword: result.tempPassword,
          tempPasswordExpiresAt: result.tempPasswordExpiresAt
        };
      } else {
        // Dev mode: use localStorage auth (password required)
        const newUser = await createUser(userData, password, auth.user?.id);
        refresh();
        return { user: newUser, tempPassword: null };
      }
    } catch (err) {
      throw new Error(err.message || 'Failed to create user');
    }
  }, [isAdmin, auth.user, refresh]);

  // Update user details
  const updateUser = useCallback(async (userId, updates) => {
    if (!isAdmin) {
      throw new Error('Permission denied: Only administrators can update users');
    }

    // Prevent changing own role
    if (userId === auth.user?.id && updates.role && updates.role !== auth.user.role) {
      throw new Error('Cannot change your own role');
    }

    // Prevent demoting or changing role of last active admin
    if (updates.role && isLastActiveAdmin(userId)) {
      throw new Error('Cannot change role: This is the last active administrator');
    }

    try {
      // FT-395 (Cluster B): use Azure API in production, localStorage in dev mode.
      // Mirrors addUser/resetPassword. The API PATCH /admin/users/:id accepts a
      // scoped body (name/email/role/rbacRole/jobTitle/active). When an RBAC role
      // is supplied, send both the mapped API role and the rbacRole metadata so the
      // server keeps the two in sync (same convention as addUser).
      if (isAzureApiConfigured()) {
        const apiUpdates = { ...updates };
        if (updates.role) {
          apiUpdates.role = RBAC_TO_API_ROLE[updates.role] || 'user';
          apiUpdates.rbacRole = updates.role;
        }
        const result = await azureApiClient.adminUpdateUser(userId, apiUpdates);
        if (!result.success) {
          throw new Error(result.error || 'Failed to update user');
        }
        await refresh();
        return result.data;
      } else {
        // Dev mode: localStorage auth
        const updated = updateUserAuth(userId, updates, auth.user?.id);
        refresh();
        return updated;
      }
    } catch (err) {
      throw new Error(err.message || 'Failed to update user');
    }
  }, [isAdmin, auth.user, refresh, isLastActiveAdmin]);

  // Deactivate user
  const deactivateUser = useCallback(async (userId) => {
    if (!isAdmin) {
      throw new Error('Permission denied: Only administrators can deactivate users');
    }

    // Prevent deactivating self
    if (userId === auth.user?.id) {
      throw new Error('Cannot deactivate your own account');
    }

    // Prevent deactivating last active admin
    if (isLastActiveAdmin(userId)) {
      throw new Error('Cannot deactivate: This is the last active administrator');
    }

    try {
      // FT-395 (Cluster B): use Azure API in production, localStorage in dev mode.
      if (isAzureApiConfigured()) {
        const result = await azureApiClient.adminDeactivateUser(userId);
        if (!result.success) {
          throw new Error(result.error || 'Failed to deactivate user');
        }
        await refresh();
      } else {
        deactivateUserAuth(userId, auth.user?.id);
        refresh();
      }
    } catch (err) {
      throw new Error(err.message || 'Failed to deactivate user');
    }
  }, [isAdmin, auth.user, refresh, isLastActiveAdmin]);

  // Reactivate user
  const reactivateUser = useCallback(async (userId) => {
    if (!isAdmin) {
      throw new Error('Permission denied: Only administrators can reactivate users');
    }

    try {
      // FT-395 (Cluster B): use Azure API in production, localStorage in dev mode.
      if (isAzureApiConfigured()) {
        const result = await azureApiClient.adminReactivateUser(userId);
        if (!result.success) {
          throw new Error(result.error || 'Failed to reactivate user');
        }
        await refresh();
      } else {
        reactivateUserAuth(userId, auth.user?.id);
        refresh();
      }
    } catch (err) {
      throw new Error(err.message || 'Failed to reactivate user');
    }
  }, [isAdmin, auth.user, refresh]);

  // Reset user password
  const resetPassword = useCallback(async (userId, newPassword) => {
    if (!isAdmin) {
      throw new Error('Permission denied: Only administrators can reset passwords');
    }

    // FT-101: Azure API resets password server-side (no newPassword needed)
    // localStorage mode requires newPassword validation
    if (!isAzureApiConfigured()) {
      const strength = validatePasswordStrength(newPassword);
      if (!strength.valid) {
        throw new Error(strength.errors.join('. '));
      }
    }

    try {
      // FT-101: Use Azure API in production, localStorage in dev mode
      if (isAzureApiConfigured()) {
        // Azure API generates a temporary password server-side
        const result = await azureApiClient.adminResetPassword(userId);
        if (!result.success) {
          throw new Error(result.error || 'Failed to reset password');
        }
        // Return the temporary password so UI can display it
        return { tempPassword: result.tempPassword };
      } else {
        // Dev mode: set password directly in localStorage
        await changePassword(userId, newPassword, auth.user?.id);
        return { tempPassword: null };
      }
    } catch (err) {
      throw new Error(err.message || 'Failed to reset password');
    }
  }, [isAdmin, auth.user]);

  // Get active users only
  const activeUsers = useMemo(() => {
    return users.filter(u => u.isActive);
  }, [users]);

  // Get users by role
  const getUsersByRole = useCallback((role) => {
    return users.filter(u => u.role === role && u.isActive);
  }, [users]);

  return {
    // State
    users,
    activeUsers,
    loading,
    error,
    isAdmin,

    // Actions
    addUser,
    updateUser,
    deactivateUser,
    reactivateUser,
    resetPassword,
    refresh,

    // Helpers
    getUsersByRole,
    ROLES
  };
};

export default useUsers;
